<template>
  <div class="page-view">

    <BasePageHeader :title="isNew ? $t('common.new') + ' ' + $t('entity.bundle') : (form.name || $t('entity.bundle'))" @back="$router.back()">
      <template #actions>
        <button v-if="!isNew" class="btn ghost sm" @click="addBundleToCheckout">
          + {{ $t('bundles.checkout') }}
        </button>
        <RunMacroButton v-if="!isNew" model-type="bundle" :model-id="bundleId" />
        <button v-if="isManagerOrAbove" class="btn primary sm" :disabled="saving || !formValid" @click="save">
          {{ saving ? $t('common.saving') : (isNew ? $t('common.create') : $t('common.save')) }}
        </button>
      </template>
    </BasePageHeader>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />

    <template v-else>

      <!-- ── Bundle details ────────────────────────────────────────── -->
      <div class="px-4 pt-5 pb-2 border-b border-[var(--color-border)]">
        <div class="section-label">{{ $t('bundles.details') }}</div>

        <BaseFormField :label="$t('common.name')" required>
          <BaseInput v-model="form.name" :placeholder="$t('ui.eGStageLightingKitA')" maxlength="200" />
        </BaseFormField>

        <BaseFormField :label="$t('bundles.sku')" required>
          <BaseInput v-model="form.sku" :placeholder="$t('ui.eGKitLightA')" maxlength="100" />
        </BaseFormField>

        <BaseFormField :label="$t('common.description')">
          <BaseTextarea v-model="form.description" :placeholder="$t('ui.whatSIncludedIntendedUse')" :rows="3" />
        </BaseFormField>

        <div class="form-row">
          <BaseFormField :label="$t('bundles.priceOverride')">
            <div class="relative">
              <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--color-muted)] text-[.9rem]">kr</span>
              <BaseInput style="padding-left: 1.75rem;" type="number" min="0" step="0.01" v-model="form.bundle_price" :placeholder="$t('ui.auto')" />
            </div>
          </BaseFormField>
          <div class="form-field flex flex-col justify-end">
            <div class="flex items-center gap-3">
              <span class="text-[.8rem] text-[var(--color-muted)]">{{ $t('bundles.useOverride') }}</span>
              <button
                class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors duration-200 shrink-0"
                :class="form.price_override ? 'bg-blue-600' : 'bg-gray-700'"
                @click="form.price_override = !form.price_override"
              >
                <span
                  class="absolute top-[3px] left-[3px] w-[18px] h-[18px] rounded-full bg-white transition-transform duration-200"
                  :class="form.price_override ? 'translate-x-5' : ''"
                />
              </button>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-3 mb-3.5">
          <span class="form-label m-0">{{ $t('common.active') }}</span>
          <button
            class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors duration-200 shrink-0"
            :class="form.is_active ? 'bg-blue-600' : 'bg-gray-700'"
            @click="form.is_active = !form.is_active"
          >
            <span
              class="absolute top-[3px] left-[3px] w-[18px] h-[18px] rounded-full bg-white transition-transform duration-200"
              :class="form.is_active ? 'translate-x-5' : ''"
            />
          </button>
        </div>

        <BaseFormField :label="$t('bundles.minAvailThreshold')" :hint="$t('bundles.minAvailThresholdHint')">
          <BaseInput type="number" min="0" v-model="form.min_availability_threshold" placeholder="0" />
        </BaseFormField>
      </div>

      <!-- ── Bundle items (existing bundles only) ──────────────────── -->
      <div v-if="!isNew" class="px-4 pt-5 pb-2 border-b border-[var(--color-border)]">
        <div class="flex items-center justify-between mb-3.5">
          <div class="section-label">{{ $t('bundles.bundleItems') }}</div>
          <button class="btn ghost sm flex items-center gap-1" @click="toggleAddItem">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-3.5 h-3.5">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            {{ showAddItem ? $t('common.cancel') : $t('common.add') }}
          </button>
        </div>

        <!-- Add item form -->
        <div v-if="showAddItem" class="bg-[var(--color-surface)] border border-[#1e3a5f] rounded-xl p-4 mb-3">
          <div class="text-sm font-bold text-blue-400 mb-3">{{ $t('bundles.addItemTitle') }}</div>
          <BaseFormField :label="$t('bundles.searchItemLabel')">
            <SearchField
              v-model="itemSearch"
              :results="mappedBundleItemResults"
              :loading="itemSearchLoading"
              :debounce="300"
              search-key="bundle-items"
              :placeholder="$t('bundles.scanPlaceholder')"
              @search="doItemSearch"
              @select="onItemSelect($event)"
            />
          </BaseFormField>

          <div v-if="selectedItem" class="bg-emerald-950/60 border border-green-800 rounded-lg px-3.5 py-2.5 mb-3">
            <div class="flex items-center gap-1.5 text-[.875rem] font-semibold text-green-300">
              <svg class="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              {{ selectedItem.name ?? '#' + selectedItem.id }}
            </div>
            <div v-if="selectedIsItemType" class="text-xs text-emerald-400/70 mt-0.5">{{ $t('bundles.anyInstanceHint') }}</div>
            <div v-else class="text-xs text-cyan-400/70 mt-0.5">{{ $t('bundles.specificInstance') }}{{ selectedItem.asset_tag ? ' · ' + selectedItem.asset_tag : '' }}</div>
            <div class="form-row mt-3">
              <BaseFormField :label="$t('bundles.quantityLabel')">
                <BaseInput type="number" min="1" v-model="newBundleItem.quantity" />
              </BaseFormField>
              <div class="form-field flex flex-col justify-end">
                <div class="flex items-center gap-3">
                  <span class="text-[.8rem] text-[var(--color-muted)]">{{ $t('bundles.optionalLabel') }}</span>
                  <button
                    class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors duration-200 shrink-0"
                    :class="newBundleItem.is_optional ? 'bg-blue-600' : 'bg-gray-700'"
                    @click="newBundleItem.is_optional = !newBundleItem.is_optional"
                  >
                    <span
                      class="absolute top-[3px] left-[3px] w-[18px] h-[18px] rounded-full bg-white transition-transform duration-200"
                      :class="newBundleItem.is_optional ? 'translate-x-5' : ''"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="addItemError" class="text-xs text-red-400 mb-2">{{ addItemError }}</div>
          <div class="flex gap-2 justify-end mt-2">
            <button class="btn ghost sm" @click="cancelAddItem">{{ $t('common.cancel') }}</button>
            <button class="btn primary sm" :disabled="addingItem || !selectedItem" @click="submitAddItem">
              {{ addingItem ? $t('common.loading') : $t('bundles.addItemBtn') }}
            </button>
          </div>
        </div>

        <!-- Items list -->
        <EmptyState v-if="itemsLoading" :message="$t('common.loading')" />
        <EmptyState v-else-if="!bundleItems.length && !showAddItem" :message="$t('bundles.noItems')" />
        <div v-else-if="bundleItems.length" class="flex flex-col gap-1.5 mt-1 pb-2">
          <div
            v-for="bi in bundleItems"
            :key="bi.id"
            class="data-card"
            :class="{ 'opacity-40 pointer-events-none': bi._removing }"
          >
            <div class="data-card__main">
              <div class="data-card__name">{{ bi.item_metadata?.item?.name ?? bi.item?.name ?? '#' + bi.id }}</div>
              <div class="flex gap-2 flex-wrap mt-0.5">
                <span v-if="bi.item && !bi.item_metadata" class="badge badge-success">{{ $t('bundles.anyInstance') }}</span>
                <span v-if="bi.item_metadata?.asset_tag" class="badge badge-muted">{{ $t('ui.assetTagLabel') }} {{ bi.item_metadata.asset_tag }}</span>
                <span v-if="bi.item_metadata?.serial_number" class="badge badge-muted">SN: {{ bi.item_metadata.serial_number }}</span>
                <span class="badge badge-muted">×{{ bi.quantity }}</span>
                <span v-if="bi.is_optional" class="badge badge-muted">{{ $t('bundles.optionalBadge') }}</span>
              </div>
            </div>
            <BaseIconButton
              variant="danger"
              :disabled="bi._removing"
              :title="$t('bundles.removeFromBundleTitle')"
              @click="onRemoveBundleItem(bi)"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[18px] h-[18px]">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6l-1 14H6L5 6"/>
                <path d="M10 11v6M14 11v6"/>
                <path d="M9 6V4h6v2"/>
              </svg>
            </BaseIconButton>
          </div>
        </div>
      </div>

      <!-- ── Danger zone ──────────────────────────────────────────── -->
      <div v-if="!isNew" class="px-4 pt-5 pb-2">
        <div class="section-label danger">{{ $t('common.dangerZone') }}</div>
        <button v-if="isManagerOrAbove" class="btn danger w-full" :disabled="deleting" @click="onDelete">
          {{ deleting ? $t('common.deleting') : $t('bundles.deleteBundle') }}
        </button>
      </div>

    </template>

    <!-- Delete confirm modal -->
    <BaseConfirmModal
      :show="showDeleteConfirm"
      :title="$t('bundles.deleteConfirmTitle')"
      :body="$t('bundles.deleteConfirmBody', { name: form.name })"
      :confirm-label="$t('common.delete')"
      :loading="deleting"
      @confirm="confirmDelete"
      @cancel="showDeleteConfirm = false"
    />

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import { usePermissions } from '@/composables/usePermissions.js'
import { useStore } from '@/stores/store'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseTextarea from '@/components/base/BaseTextarea.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import RunMacroButton from '@/components/macros/RunMacroButton.vue'
import BaseConfirmModal from '@/components/base/BaseConfirmModal.vue'
import BaseIconButton from '@/components/base/BaseIconButton.vue'
import SearchField from '@/components/search/SearchField.vue'
import {
  getBundle, createBundle, updateBundle, deleteBundle,
  getBundleItems, createBundleItem, deleteBundleItem,
  getSearchResults,
} from '@/api/api.js'

const router = useRouter()
const route  = useRoute()
const toast  = useToast()
const { t }  = useI18n()
const store  = useStore()
const { isManagerOrAbove } = usePermissions()

const bundleId = computed(() => route.params.bundleId)
const isNew    = computed(() => !bundleId.value || bundleId.value === 'new')

// ── form ───────────────────────────────────────────────────────────────
const form = ref({
  name: '', sku: '', description: '', bundle_price: '',
  price_override: false, is_active: true, min_availability_threshold: '',
})

const formValid = computed(() => form.value.name.trim() && form.value.sku.trim())

// ── load ──────────────────────────────────────────────────────────────
const loading   = ref(false)
const loadError = ref(null)
const saving    = ref(false)
const deleting  = ref(false)
const showDeleteConfirm = ref(false)

async function load() {
  if (isNew.value) return
  loading.value   = true
  loadError.value = null
  try {
    const bundle = await getBundle(bundleId.value)
    form.value.name                     = bundle.name ?? ''
    form.value.sku                      = bundle.sku  ?? ''
    form.value.description              = bundle.description ?? ''
    form.value.bundle_price             = bundle.bundle_price ?? ''
    form.value.price_override           = bundle.price_override ?? false
    form.value.is_active                = bundle.is_active ?? true
    form.value.min_availability_threshold = bundle.min_availability_threshold ?? ''
  } catch {
    loadError.value = t('bundles.loadBundleFailed')
  } finally {
    loading.value = false
  }
  await loadBundleItems()
}

// ── bundle items ──────────────────────────────────────────────────────
const bundleItems  = ref([])
const itemsLoading = ref(false)
const showAddItem  = ref(false)
const addingItem   = ref(false)
const addItemError = ref(null)
const itemSearch         = ref('')
const itemSearchLoading  = ref(false)
const itemSearchResults  = ref([])
const itemTypeResults    = ref([])
const selectedItem       = ref(null)
const selectedIsItemType = ref(false)
const newBundleItem      = ref({ quantity: 1, is_optional: false })
let suppressNextItemSearch = false

const mappedBundleItemResults = computed(() => {
  const results = []
  // Item types (generic — "any available instance")
  for (const r of itemTypeResults.value) {
    results.push({
      id: `item-${r.object_id}`,
      label: r.display?.name ?? t('ui.itemNumber', { id: r.object_id }),
      sublabel: t('bundles.anyInstance'),
      badge: t('common.type'),
      meta: { id: r.object_id, name: r.display?.name },
      _isItemType: true,
    })
  }
  // Specific instances (ItemMetadata)
  for (const r of itemSearchResults.value) {
    results.push({
      id: `meta-${r.object_id}`,
      label: r.display?.name ?? `#${r.object_id}`,
      sublabel: r.display?.subtitle || t('bundles.specificInstance'),
      badge: t('itemMetadata.title'),
      meta: { id: r.object_id, name: r.display?.name, asset_tag: r.display?.subtitle },
      _isItemType: false,
    })
  }
  return results
})

async function doItemSearch(q) {
  if (suppressNextItemSearch) { suppressNextItemSearch = false; return }
  selectedItem.value = null
  selectedIsItemType.value = false
  if (!q || q.length < 2) { itemSearchResults.value = []; itemTypeResults.value = []; return }
  itemSearchLoading.value = true
  try {
    const res = await getSearchResults(q, { limit: 10 })
    const raw = res.results ?? {}
    itemTypeResults.value = raw.item ?? []
    itemSearchResults.value = raw.item_metadata ?? []
  } catch {
    itemSearchResults.value = []
    itemTypeResults.value = []
  } finally {
    itemSearchLoading.value = false
  }
}

function onItemSelect(result) {
  suppressNextItemSearch = true
  selectedIsItemType.value = result._isItemType
  selectSearchResult(result.meta, result._isItemType)
}

async function loadBundleItems() {
  if (isNew.value) return
  itemsLoading.value = true
  try {
    const res = await getBundleItems(bundleId.value)
    bundleItems.value = res.results ?? res ?? []
  } catch {
    bundleItems.value = []
  } finally {
    itemsLoading.value = false
  }
}

function toggleAddItem() {
  showAddItem.value = !showAddItem.value
  if (showAddItem.value) {
    itemSearch.value = ''
    itemSearchResults.value = []
    itemTypeResults.value = []
    selectedItem.value = null
    selectedIsItemType.value = false
    newBundleItem.value = { quantity: 1, is_optional: false }
    addItemError.value = null
  }
}

function cancelAddItem() { showAddItem.value = false }

function selectSearchResult(r, isItemType = false) {
  selectedItem.value  = r
  selectedIsItemType.value = isItemType
  itemSearch.value    = r.name ?? r.asset_tag ?? '#' + r.id
  itemSearchResults.value = []
  itemTypeResults.value = []
}

async function submitAddItem() {
  if (!selectedItem.value || addingItem.value) return
  addingItem.value   = true
  addItemError.value = null
  try {
    const payload = {
      bundle:      parseInt(bundleId.value, 10),
      quantity:    parseInt(newBundleItem.value.quantity, 10) || 1,
      is_optional: newBundleItem.value.is_optional,
    }
    if (selectedIsItemType.value) {
      payload.item = selectedItem.value.id
    } else {
      payload.item_metadata = selectedItem.value.id
    }
    const res = await createBundleItem(payload)
    // enrich with nested detail for display
    if (selectedIsItemType.value) {
      bundleItems.value.push({ ...res, item: selectedItem.value, item_metadata: null })
    } else {
      bundleItems.value.push({ ...res, item_metadata: selectedItem.value, item: null })
    }
    toast.success(t('bundles.addItemSuccess'))
    showAddItem.value = false
  } catch (e) {
    addItemError.value = e?.data?.detail ?? e?.data?.item_metadata?.[0] ?? e?.data?.item?.[0] ?? e?.data?.non_field_errors?.[0] ?? e?.message ?? t('bundles.addItemFailed')
  } finally {
    addingItem.value = false
  }
}

async function onRemoveBundleItem(bi) {
  bi._removing = true
  try {
    await deleteBundleItem(bi.id)
    bundleItems.value = bundleItems.value.filter(i => i.id !== bi.id)
    toast.success(t('bundles.removeItemSuccess'))
  } catch {
    bi._removing = false
    toast.error(t('bundles.removeItemFailed'))
  }
}

// ── save ──────────────────────────────────────────────────────────────
async function save() {
  if (!formValid.value || saving.value) return
  saving.value = true
  try {
    const payload = {
      name:          form.value.name.trim(),
      sku:           form.value.sku.trim(),
      description:   form.value.description,
      price_override: form.value.price_override,
      is_active:     form.value.is_active,
    }
    if (form.value.bundle_price !== '') payload.bundle_price = form.value.bundle_price
    if (form.value.min_availability_threshold !== '')
      payload.min_availability_threshold = parseInt(form.value.min_availability_threshold, 10)

    if (isNew.value) {
      const created = await createBundle(payload)
      toast.success(t('bundles.created'))
      router.replace(`/bundles/${created.id}`)
    } else {
      await updateBundle(bundleId.value, payload)
      toast.success(t('bundles.saved'))
    }
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.data?.name?.[0] ?? e?.message ?? t('bundles.deleteSaveFailed'))
  } finally {
    saving.value = false
  }
}

// ── delete ────────────────────────────────────────────────────────────
function onDelete() { showDeleteConfirm.value = true }

function addBundleToCheckout() {
  store.addCheckoutBundle({
    id: bundleId.value,
    name: form.value.name,
    sku: form.value.sku,
    itemCount: bundleItems.value.length,
  })
  toast.success(t('bundles.addedToCheckoutToast', { name: form.value.name }))
}

async function confirmDelete() {
  deleting.value = true
  try {
    await deleteBundle(bundleId.value)
    toast.success(t('bundles.deleted'))
    router.replace('/bundles')
  } catch {
    toast.error(t('bundles.deleteFailed'))
    deleting.value = false
    showDeleteConfirm.value = false
  }
}

// ── lifecycle ─────────────────────────────────────────────────────────
onMounted(load)
watch(bundleId, (newId, oldId) => { if (newId && newId !== oldId && newId !== 'new') load() })
</script>


